import type { ReactNode } from 'react';
import { cn } from '@/shared/helpers/cn';
import { Skeleton } from '@/shared/components/Skeleton';
import styles from './Table.module.scss';

export interface TableColumn<T> {
  /** Identificador estavel da coluna. */
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  /** Numerico alinha a direita e usa `tabular-nums`. */
  numeric?: boolean;
  /** Oculta esta coluna no card de mobile — util para acoes duplicadas. */
  hideOnCard?: boolean;
  /** Largura sugerida em desktop. */
  width?: string;
}

export interface TableProps<T> {
  /** Descricao da tabela para leitores de tela. */
  caption: string;
  columns: readonly TableColumn<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  /** Titulo em destaque no card de mobile. */
  cardTitle?: (row: T) => ReactNode;
  /** Acao primaria no rodape do card de mobile. */
  cardAction?: (row: T) => ReactNode;
  loading?: boolean;
  /** Exibido quando nao ha linhas. Deve dizer o que fazer, nao so "sem dados". */
  emptyState?: ReactNode;
  /**
   * Sem moldura: o cabecalho fica solto sobre o fundo da pagina e so as linhas
   * ficam em branco. E o desenho das listagens do sistema atual.
   */
  plain?: boolean;
  className?: string;
}

const SKELETON_ROWS = 5;

/**
 * Tabela do sistema, com as **tres estrategias responsivas** obrigatorias.
 *
 * - `>= lg` — tabela nativa, cabecalho `sticky` em micro-label.
 * - `md` — rolagem horizontal **no contêiner**, com a primeira coluna fixa e
 *   sombra a direita indicando o corte.
 * - `< md` — cada linha vira um card empilhado: rotulo a esquerda, valor a
 *   direita, titulo do registro em destaque, acao primaria no rodape.
 *
 * As duas formas coexistem no DOM e sao alternadas por `display`, que remove a
 * versao oculta tambem da arvore de acessibilidade. Em nenhum momento a fonte
 * encolhe para caber a tabela no telefone.
 */
export function Table<T>({
  caption,
  columns,
  rows,
  rowKey,
  cardTitle,
  cardAction,
  loading,
  emptyState,
  plain,
  className,
}: TableProps<T>) {
  if (loading) {
    return (
      <div className={cn(styles.root, className)} aria-busy="true">
        <span className={styles.srOnly}>Carregando {caption}</span>
        {Array.from({ length: SKELETON_ROWS }, (_, index) => (
          <Skeleton key={index} height="44px" className={styles.skeletonRow} />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return <div className={cn(styles.root, className)}>{emptyState}</div>;
  }

  const cardColumns = columns.filter((column) => !column.hideOnCard);

  return (
    <div className={cn(styles.root, className)}>
      {/* >= md: tabela, com rolagem contida e primeira coluna fixa em md */}
      <div className={cn(styles.scroller, plain && styles.scrollerPlain)}>
        <table className={styles.table}>
          <caption className={styles.srOnly}>{caption}</caption>
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th
                  key={column.key}
                  scope="col"
                  style={column.width ? { inlineSize: column.width } : undefined}
                  className={cn(
                    styles.th,
                    column.numeric && styles.numeric,
                    index === 0 && styles.stickyCell,
                  )}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={rowKey(row)} className={styles.tr}>
                {columns.map((column, index) => (
                  <td
                    key={column.key}
                    className={cn(
                      styles.td,
                      column.numeric && styles.numeric,
                      index === 0 && styles.stickyCell,
                    )}
                  >
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* < md: uma lista de cards, um por registro */}
      <ul className={styles.cards}>
        {rows.map((row) => (
          <li key={rowKey(row)} className={styles.card}>
            {cardTitle ? <p className={styles.cardTitle}>{cardTitle(row)}</p> : null}
            <dl className={styles.cardBody}>
              {cardColumns.map((column) => (
                <div key={column.key} className={styles.cardRow}>
                  <dt className={styles.cardLabel}>{column.header}</dt>
                  <dd className={cn(styles.cardValue, column.numeric && styles.numeric)}>
                    {column.render(row)}
                  </dd>
                </div>
              ))}
            </dl>
            {cardAction ? <div className={styles.cardFooter}>{cardAction(row)}</div> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
